import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRight, Save, Send, Trash2, Archive, Loader2, Percent, Settings,
  Search as SearchIcon, Image as ImageIcon, AlertTriangle, DollarSign,
  Calendar, Link2,
} from 'lucide-react';
import { offersAPI } from '../../services/api';
import MediaPicker from '../../components/media/MediaPicker';
import PageSeoPanel from '../../components/website-builder/PageSeoPanel';

/**
 * OfferEditor — Phase 6 full-page editor for a promotional Offer.
 *
 * Mirrors BlogPostEditor: create/edit via :id param, tabs for
 * content / pricing & duration / SEO / settings.
 *
 * relatedPackageIds + relatedTestIds are intentionally read-only with a
 * "coming soon" hint — they wire to the LIS catalog in a future phase.
 */

const SLUG_REGEX = /^[\u0600-\u06FFa-z0-9](?:[\u0600-\u06FFa-z0-9-]*[\u0600-\u06FFa-z0-9])?$/i;

const EMPTY_FORM = {
  slug: '',
  title: { ar: '', en: '' },
  description: { ar: '', en: '' },
  badge: { ar: '', en: '' },
  imageUrl: '',
  imageAlt: '',
  priceBefore: '',
  priceAfter: '',
  currency: 'SAR',
  savingsLabel: { ar: '', en: '' },
  validFrom: '',
  validTo: '',
  relatedPackageIds: [],
  relatedTestIds: [],
  ctaText: { ar: '', en: '' },
  ctaUrl: '',
  isFeatured: false,
  sortOrder: 100,
  seo: {
    title: { ar: '', en: '' },
    description: { ar: '', en: '' },
    keywords: [],
    ogImageUrl: '',
    canonicalUrl: '',
    noindex: false,
  },
};

function toInputDateTime(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    // "YYYY-MM-DDTHH:mm" as required by datetime-local input
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function cloneForm(source) {
  const s = source || {};
  return {
    slug: s.slug || '',
    title: { ar: s.title?.ar || '', en: s.title?.en || '' },
    description: { ar: s.description?.ar || '', en: s.description?.en || '' },
    badge: { ar: s.badge?.ar || '', en: s.badge?.en || '' },
    imageUrl: s.imageUrl || '',
    imageAlt: s.imageAlt || '',
    priceBefore: s.priceBefore != null ? String(s.priceBefore) : '',
    priceAfter: s.priceAfter != null ? String(s.priceAfter) : '',
    currency: s.currency || 'SAR',
    savingsLabel: { ar: s.savingsLabel?.ar || '', en: s.savingsLabel?.en || '' },
    validFrom: toInputDateTime(s.validFrom),
    validTo: toInputDateTime(s.validTo),
    relatedPackageIds: Array.isArray(s.relatedPackageIds) ? [...s.relatedPackageIds] : [],
    relatedTestIds: Array.isArray(s.relatedTestIds) ? [...s.relatedTestIds] : [],
    ctaText: { ar: s.ctaText?.ar || '', en: s.ctaText?.en || '' },
    ctaUrl: s.ctaUrl || '',
    isFeatured: !!s.isFeatured,
    sortOrder: Number.isFinite(Number(s.sortOrder)) ? Number(s.sortOrder) : 100,
    seo: {
      title: { ar: s.seo?.title?.ar || '', en: s.seo?.title?.en || '' },
      description: { ar: s.seo?.description?.ar || '', en: s.seo?.description?.en || '' },
      keywords: Array.isArray(s.seo?.keywords) ? [...s.seo.keywords] : [],
      ogImageUrl: s.seo?.ogImageUrl || '',
      canonicalUrl: s.seo?.canonicalUrl || '',
      noindex: !!s.seo?.noindex,
    },
  };
}

function buildPayload(form) {
  const payload = {
    title: { ar: form.title.ar || '', en: form.title.en || '' },
    description: { ar: form.description.ar || '', en: form.description.en || '' },
    badge: { ar: form.badge.ar || '', en: form.badge.en || '' },
    imageUrl: form.imageUrl || '',
    imageAlt: form.imageAlt || '',
    priceBefore: form.priceBefore === '' ? 0 : Number(form.priceBefore) || 0,
    priceAfter: form.priceAfter === '' ? 0 : Number(form.priceAfter) || 0,
    currency: form.currency || 'SAR',
    savingsLabel: { ar: form.savingsLabel.ar || '', en: form.savingsLabel.en || '' },
    validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : null,
    validTo: form.validTo ? new Date(form.validTo).toISOString() : null,
    relatedPackageIds: Array.isArray(form.relatedPackageIds) ? form.relatedPackageIds : [],
    relatedTestIds: Array.isArray(form.relatedTestIds) ? form.relatedTestIds : [],
    ctaText: { ar: form.ctaText.ar || '', en: form.ctaText.en || '' },
    ctaUrl: form.ctaUrl || '',
    isFeatured: !!form.isFeatured,
    sortOrder: Number.isFinite(Number(form.sortOrder)) ? Number(form.sortOrder) : 100,
    seo: {
      title: { ar: form.seo.title.ar || '', en: form.seo.title.en || '' },
      description: { ar: form.seo.description.ar || '', en: form.seo.description.en || '' },
      keywords: Array.isArray(form.seo.keywords) ? form.seo.keywords.filter(Boolean) : [],
      ogImageUrl: form.seo.ogImageUrl || '',
      canonicalUrl: form.seo.canonicalUrl || '',
      noindex: !!form.seo.noindex,
    },
  };
  if (form.slug) payload.slug = form.slug;
  return payload;
}

function mapErrorToArabic(err, fallback) {
  const code = err?.response?.data?.code;
  if (code === 'SLUG_CONFLICT') return 'الرابط مستخدم مسبقاً — اختر واحداً آخر';
  if (code === 'INVALID_SLUG') return 'الرابط غير صالح — حروف وأرقام وشرطات فقط';
  if (code === 'TITLE_OR_SLUG_REQUIRED') return 'أدخل عنواناً أو رابطاً للعرض';
  if (code === 'INVALID_DATE_RANGE') return 'تاريخ البداية يجب أن يكون قبل تاريخ النهاية';
  return err?.response?.data?.message || fallback;
}

function formatRelativeAr(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffHr = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffMin) < 1) return 'قبل لحظات';
    if (Math.abs(diffMin) < 60) return `قبل ${diffMin} د`;
    if (Math.abs(diffHr) < 24) return `قبل ${diffHr} س`;
    if (Math.abs(diffDay) < 30) return `قبل ${diffDay} يوم`;
    return d.toLocaleDateString('ar-SA');
  } catch {
    return '';
  }
}

export default function OfferEditor() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const isNew = !paramId || paramId === 'new';

  const [currentId, setCurrentId] = useState(isNew ? null : paramId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [baseline, setBaseline] = useState(EMPTY_FORM);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);

  const [tab, setTab] = useState('content');
  const [contentLocale, setContentLocale] = useState('ar');

  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);

  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    setCurrentId(isNew ? null : paramId);
  }, [paramId, isNew]);

  const fetchOffer = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await offersAPI.get(id);
      const data = resp.data?.data || resp.data;
      if (!aliveRef.current) return;
      const next = cloneForm(data);
      setForm(next);
      setBaseline(next);
      setMeta(data);
    } catch (err) {
      if (!aliveRef.current) return;
      setLoadError(err?.response?.data?.message || 'فشل تحميل العرض');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentId) {
      setForm(EMPTY_FORM);
      setBaseline(EMPTY_FORM);
      setMeta(null);
      setLoading(false);
      return;
    }
    fetchOffer(currentId);
  }, [currentId, fetchOffer]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline]
  );

  const seoDirty = useMemo(
    () => JSON.stringify(form.seo) !== JSON.stringify(baseline.seo),
    [form.seo, baseline.seo]
  );

  const statusLabel = (() => {
    const s = meta?.status;
    if (s === 'published') return 'منشور';
    if (s === 'draft') return 'مسودة';
    if (s === 'archived') return 'مؤرشف';
    return currentId ? 'مسودة' : 'جديد';
  })();

  const statusBadge = (() => {
    const s = meta?.status;
    if (s === 'published' && !dirty) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s === 'archived') return 'bg-gray-100 text-gray-600 border-gray-200';
    return 'bg-amber-50 text-amber-700 border-amber-200';
  })();

  const priceBeforeNum = Number(form.priceBefore) || 0;
  const priceAfterNum = Number(form.priceAfter) || 0;
  const savingsPercent = (priceBeforeNum > 0 && priceAfterNum >= 0 && priceAfterNum < priceBeforeNum)
    ? Math.round(((priceBeforeNum - priceAfterNum) / priceBeforeNum) * 100)
    : null;

  const dateRangeInvalid = !!(form.validFrom && form.validTo && new Date(form.validFrom) >= new Date(form.validTo));
  const priceInvalid = !!(priceBeforeNum > 0 && priceAfterNum > priceBeforeNum);

  const validateBeforeSave = useCallback(() => {
    if (!form.title.ar && !form.title.en) {
      toast.error('أدخل عنوان العرض بالعربية أو الإنجليزية');
      return false;
    }
    if (form.slug && !SLUG_REGEX.test(form.slug)) {
      toast.error('الرابط غير صالح — حروف صغيرة وأرقام وشرطات فقط');
      return false;
    }
    if (priceInvalid) {
      toast.error('السعر بعد الخصم يجب أن يكون أقل من السعر قبل الخصم');
      return false;
    }
    if (dateRangeInvalid) {
      toast.error('تاريخ البداية يجب أن يكون قبل تاريخ النهاية');
      return false;
    }
    return true;
  }, [form.title.ar, form.title.en, form.slug, priceInvalid, dateRangeInvalid]);

  const saveDraft = useCallback(async ({ silent = false } = {}) => {
    if (!validateBeforeSave()) return null;
    setSaving(true);
    try {
      const payload = buildPayload(form);
      let saved;
      if (!currentId) {
        const resp = await offersAPI.create(payload);
        saved = resp.data?.data || resp.data;
        if (!silent) toast.success('تم إنشاء العرض');
        if (saved?._id) {
          const next = cloneForm(saved);
          if (aliveRef.current) {
            setForm(next);
            setBaseline(next);
            setMeta(saved);
            setCurrentId(saved._id);
          }
          navigate(`/settings/website/offers/${saved._id}`, { replace: true });
        }
      } else {
        const resp = await offersAPI.update(currentId, payload);
        saved = resp.data?.data || resp.data;
        if (!silent) toast.success('تم حفظ المسودة');
        if (saved?._id && aliveRef.current) {
          const next = cloneForm(saved);
          setForm(next);
          setBaseline(next);
          setMeta(saved);
        }
      }
      return saved;
    } catch (err) {
      toast.error(mapErrorToArabic(err, 'فشل الحفظ'));
      return null;
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  }, [form, currentId, navigate, validateBeforeSave]);

  const publishOffer = async () => {
    if (publishing) return;
    if (!validateBeforeSave()) return;
    setPublishing(true);
    try {
      let targetId = currentId;
      if (!targetId || dirty) {
        const saved = await saveDraft({ silent: true });
        if (!saved?._id) {
          setPublishing(false);
          return;
        }
        targetId = saved._id;
      }
      await offersAPI.publish(targetId);
      toast.success('تم نشر العرض');
      await fetchOffer(targetId);
    } catch (err) {
      toast.error(mapErrorToArabic(err, 'فشل النشر'));
    } finally {
      if (aliveRef.current) setPublishing(false);
    }
  };

  const archiveOffer = async () => {
    if (archiving || !currentId) return;
    setArchiving(true);
    try {
      await offersAPI.archive(currentId);
      toast.success('تم أرشفة العرض');
      await fetchOffer(currentId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الأرشفة');
    } finally {
      if (aliveRef.current) setArchiving(false);
    }
  };

  const deleteOffer = async () => {
    if (!currentId) {
      navigate('/settings/website/offers');
      return;
    }
    setDeleting(true);
    try {
      await offersAPI.remove(currentId);
      toast.success('تم حذف العرض');
      navigate('/settings/website/offers');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الحذف');
      if (aliveRef.current) setDeleting(false);
    }
  };

  const setField = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const setNested = (key, subKey, value) =>
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], [subKey]: value } }));

  const onPickImage = (asset) => {
    const url = asset?.publicUrl || asset?.url || '';
    setForm((prev) => ({ ...prev, imageUrl: url }));
    setPickerOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" dir="rtl">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container mx-auto px-6 py-10" dir="rtl">
        <div className="max-w-2xl mx-auto p-8 rounded-2xl border border-blue-100 bg-blue-50 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-blue-500" />
          <p className="text-sm text-blue-700">{loadError}</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              onClick={() => currentId && fetchOffer(currentId)}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
            >
              أعد المحاولة
            </button>
            <button
              onClick={() => navigate('/settings/website/offers')}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-sm text-gray-700"
            >
              العودة للقائمة
            </button>
          </div>
        </div>
      </div>
    );
  }

  const contentLabel = contentLocale === 'ar' ? 'العربية' : 'English';

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Sticky status bar */}
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#F8FAFC]/85 backdrop-blur border-b border-gray-100 mb-5">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate('/settings/website/offers')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              <ArrowRight className="w-4 h-4" />
              عودة
            </button>

            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm shrink-0">
                <Percent className="w-4.5 h-4.5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-bold text-gray-800 truncate">
                  {form.title.ar || form.title.en || (currentId ? 'بدون عنوان' : 'عرض جديد')}
                </h1>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusBadge}`}>
                    {statusLabel}
                  </span>
                  {meta?.updatedAt && (
                    <span>آخر حفظ: {formatRelativeAr(meta.updatedAt)}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1" />

            <button
              onClick={() => saveDraft()}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-blue-50 border border-blue-100 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              حفظ مسودة
            </button>

            <button
              type="button"
              disabled
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-400 cursor-not-allowed"
              title="قيد التطوير"
            >
              <SearchIcon className="w-4 h-4" />
              معاينة (قريباً)
            </button>

            {meta?.status === 'published' ? (
              <button
                onClick={archiveOffer}
                disabled={archiving}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                أرشفة
              </button>
            ) : null}

            <button
              onClick={publishOffer}
              disabled={publishing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 shadow-sm disabled:opacity-50"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              نشر
            </button>

            <button
              onClick={() => setConfirmDelete(true)}
              className="p-2 rounded-xl border border-blue-200 text-blue-600 hover:bg-blue-50"
              title="حذف"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-5">
          <div className="flex items-center gap-1 p-1.5 border-b border-gray-100 bg-gray-50/60 flex-wrap">
            {[
              { value: 'content', label: 'المحتوى', icon: Percent },
              { value: 'pricing', label: 'السعر والمدة', icon: DollarSign },
              { value: 'seo', label: 'SEO', icon: SearchIcon },
              { value: 'settings', label: 'إعدادات', icon: Settings },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={[
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  tab === value
                    ? 'bg-white text-blue-700 shadow-sm border border-gray-100'
                    : 'text-gray-600 hover:text-gray-800',
                ].join(' ')}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Locale switch (shared for content/pricing-labels) */}
        {(tab === 'content' || tab === 'pricing') && (
          <div className="flex items-center gap-2 mb-4">
            <div className="flex rounded-lg border border-gray-200 p-0.5 bg-white">
              {[
                { value: 'ar', label: 'العربية' },
                { value: 'en', label: 'English' },
              ].map((l) => (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => setContentLocale(l.value)}
                  className={[
                    'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                    contentLocale === l.value ? 'bg-blue-500 text-white' : 'text-gray-600 hover:text-gray-800',
                  ].join(' ')}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-400">الحقول النصية ثنائية اللغة</span>
          </div>
        )}

        {/* Tab: Content */}
        {tab === 'content' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="space-y-5"
          >
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  عنوان العرض ({contentLabel})
                </label>
                <input
                  value={form.title[contentLocale] || ''}
                  onChange={(e) => setNested('title', contentLocale, e.target.value)}
                  placeholder={contentLocale === 'ar' ? 'مثال: فحص شامل بسعر مخفض' : 'e.g. Comprehensive checkup, discounted'}
                  dir={contentLocale === 'en' ? 'ltr' : 'rtl'}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  الوصف ({contentLabel})
                </label>
                <textarea
                  value={form.description[contentLocale] || ''}
                  onChange={(e) => setNested('description', contentLocale, e.target.value)}
                  placeholder="اشرح تفاصيل العرض والفائدة"
                  rows={4}
                  dir={contentLocale === 'en' ? 'ltr' : 'rtl'}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  شارة العرض ({contentLabel})
                </label>
                <input
                  value={form.badge[contentLocale] || ''}
                  onChange={(e) => setNested('badge', contentLocale, e.target.value)}
                  placeholder={contentLocale === 'ar' ? 'مثال: الأكثر طلباً' : 'e.g. Best seller'}
                  dir={contentLocale === 'en' ? 'ltr' : 'rtl'}
                  className="w-full max-w-sm px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Image */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">صورة العرض</h3>
              </div>

              {form.imageUrl ? (
                <div className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
                  <img
                    src={form.imageUrl}
                    alt={form.imageAlt || 'offer'}
                    className="w-28 h-28 object-cover rounded-lg shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 font-mono truncate" dir="ltr">
                      {form.imageUrl}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-700"
                      >
                        تغيير
                      </button>
                      <button
                        type="button"
                        onClick={() => setField({ imageUrl: '' })}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-600"
                      >
                        إزالة
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="w-full flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/40 transition-colors text-sm text-gray-500"
                >
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                  اختر صورة من المكتبة
                </button>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">النص البديل (alt)</label>
                <input
                  value={form.imageAlt}
                  onChange={(e) => setField({ imageAlt: e.target.value })}
                  placeholder="وصف قصير للصورة"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>

            {/* CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <h3 className="text-sm font-bold text-gray-800">زر الإجراء (CTA)</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    نص الزر ({contentLabel})
                  </label>
                  <input
                    value={form.ctaText[contentLocale] || ''}
                    onChange={(e) => setNested('ctaText', contentLocale, e.target.value)}
                    placeholder={contentLocale === 'ar' ? 'مثال: احجز الآن' : 'e.g. Book now'}
                    dir={contentLocale === 'en' ? 'ltr' : 'rtl'}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">رابط الزر (URL)</label>
                  <input
                    value={form.ctaUrl}
                    onChange={(e) => setField({ ctaUrl: e.target.value })}
                    placeholder="https://..."
                    dir="ltr"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tab: Pricing & Duration */}
        {tab === 'pricing' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="space-y-5"
          >
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">السعر</h3>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">السعر قبل الخصم</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.priceBefore}
                    onChange={(e) => setField({ priceBefore: e.target.value })}
                    placeholder="0"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">السعر بعد الخصم</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.priceAfter}
                    onChange={(e) => setField({ priceAfter: e.target.value })}
                    placeholder="0"
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                      priceInvalid
                        ? 'border-blue-400 focus:ring-blue-500/20 focus:border-blue-500 bg-blue-50/30'
                        : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">العملة</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setField({ currency: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="SAR">SAR — ريال سعودي</option>
                    <option value="USD">USD — دولار</option>
                    <option value="AED">AED — درهم إماراتي</option>
                    <option value="EGP">EGP — جنيه مصري</option>
                  </select>
                </div>
              </div>

              {priceInvalid && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-700">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  السعر بعد الخصم يجب أن يكون أقل من السعر قبل الخصم.
                </div>
              )}

              {savingsPercent != null && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-100 text-xs text-emerald-700 font-medium">
                  <Percent className="w-3.5 h-3.5" />
                  نسبة الخصم المحسوبة: {savingsPercent}%
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    نص التوفير (العربية)
                  </label>
                  <input
                    value={form.savingsLabel.ar}
                    onChange={(e) => setNested('savingsLabel', 'ar', e.target.value)}
                    placeholder="مثال: وفّر 50٪"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Savings label (English)
                  </label>
                  <input
                    value={form.savingsLabel.en}
                    onChange={(e) => setNested('savingsLabel', 'en', e.target.value)}
                    placeholder="e.g. Save 50%"
                    dir="ltr"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">فترة الصلاحية</h3>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">من تاريخ</label>
                  <input
                    type="datetime-local"
                    value={form.validFrom}
                    onChange={(e) => setField({ validFrom: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                      dateRangeInvalid
                        ? 'border-blue-400 focus:ring-blue-500/20 focus:border-blue-500 bg-blue-50/30'
                        : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">إلى تاريخ</label>
                  <input
                    type="datetime-local"
                    value={form.validTo}
                    onChange={(e) => setField({ validTo: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                      dateRangeInvalid
                        ? 'border-blue-400 focus:ring-blue-500/20 focus:border-blue-500 bg-blue-50/30'
                        : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              {dateRangeInvalid && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-700">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  تاريخ البداية يجب أن يكون قبل تاريخ النهاية.
                </div>
              )}
              <p className="text-xs text-gray-400">
                اتركهما فارغين ليكون العرض ساري المفعول دون حد زمني. التوقيت المحلي.
              </p>
            </div>
          </motion.div>
        )}

        {/* Tab: SEO */}
        {tab === 'seo' && (
          <PageSeoPanel
            value={form.seo}
            onChange={(v) => setForm((prev) => ({ ...prev, seo: v }))}
            pageLabel={form.title.ar || form.title.en || 'عرض جديد'}
            pageKey="offer"
            dirty={seoDirty}
            saving={saving}
            onSave={() => saveDraft()}
          />
        )}

        {/* Tab: Settings */}
        {tab === 'settings' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="space-y-5"
          >
            {/* Curation — controls which offer gets the featured slot on the
                public site + manual ordering for the rest. */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <h3 className="text-sm font-bold text-gray-800">الترتيب والإبراز</h3>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.isFeatured}
                  onChange={(e) => setField({ isFeatured: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20"
                />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-gray-800">
                    عرض مميّز
                  </span>
                  <span className="block text-xs text-gray-500 mt-1">
                    يظهر هذا العرض في المقدّمة على صفحة العروض وفي القسم البارز
                    في الصفحة الرئيسية. ميّز عرضاً واحداً أو أكثر.
                  </span>
                </span>
              </label>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  ترتيب العرض (رقم أصغر = ظاهر أولاً)
                </label>
                <input
                  type="number"
                  value={form.sortOrder ?? 100}
                  onChange={(e) => setField({ sortOrder: Number(e.target.value) || 100 })}
                  className="w-32 px-3 py-2 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-sm font-bold text-gray-800 mb-3">رابط العرض (slug)</h3>
              <input
                value={form.slug}
                onChange={(e) => setField({ slug: e.target.value })}
                placeholder="اتركه فارغاً ليُنشأ تلقائياً من العنوان"
                dir="ltr"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              {meta?.status === 'published' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mt-2">
                  لا ينصح بتغيير الرابط بعد النشر — سيُكسر أي ارتباط خارجي.
                </p>
              )}
              <p className="text-xs text-gray-400 mt-2">
                حروف عربية أو إنجليزية صغيرة، أرقام وشرطات فقط.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-bold text-gray-800">ربط الباقات والفحوصات</h3>
              </div>
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                <p className="text-sm text-gray-500">
                  ربط الباقات والفحوصات — قريباً
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  سيتم ربط العرض بباقات وفحوصات من كتالوج مختبرك في مرحلة قادمة.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Media picker */}
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={onPickImage}
        folder="offers"
      />

      {/* Confirm delete */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-800">تأكيد حذف العرض</h3>
                <p className="text-sm text-gray-600 mt-1">
                  سيتم حذف العرض نهائياً. لا يمكن التراجع.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                onClick={deleteOffer}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
